import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { userApi } from 'src/Services/API';
import {
  UpdateNotificationPreferencesDto,
  UserWithPrivateInfoDto,
} from 'src/Services/API/generated';

interface MutationContext {
  previousUserData: UserWithPrivateInfoDto | undefined;
}

export const useUpdateFeaturePreferences = (
  options?: UseMutationOptions<
    UserWithPrivateInfoDto,
    unknown,
    UpdateNotificationPreferencesDto,
    MutationContext | undefined
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    UserWithPrivateInfoDto,
    unknown,
    UpdateNotificationPreferencesDto,
    MutationContext | undefined
  >(
    'update-feature-preferences',
    async data => {
      const response = await userApi.userControllerUpdateNotificationPreferences(data);
      return response.data;
    },
    {
      onMutate: async (newPreferences): Promise<MutationContext | undefined> => {
        await queryClient.cancelQueries(['fetch--user']);
        const previousUserData = queryClient.getQueryData<UserWithPrivateInfoDto>(['fetch-user']);
        if (previousUserData) {
          const updatedUserData: UserWithPrivateInfoDto = {
            ...previousUserData,
            ...newPreferences,
          };
          queryClient.setQueryData<UserWithPrivateInfoDto>(['fetch-user'], updatedUserData);
        }
        return { previousUserData };
      },
      onError: (err, variables, context) => {
        if (context?.previousUserData) {
          queryClient.setQueryData(['fetch-user'], context.previousUserData);
        }
        options?.onError?.(err, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries(['fetch-user']);
      },
      ...options,
    }
  );
};
